// src/components/Mypage/ChildProfileModal.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { axiosInstance } from '../../utils/axios';
import AlertModal from '../common/AlertModal';
import { convertHeicToJpeg, isHeicFile } from '../../utils/imageConverter';

const CURRENT_YEAR = new Date().getFullYear();
const YEARS  = Array.from({ length: 20 }, (_, i) => CURRENT_YEAR - i);
const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);
const DAYS   = Array.from({ length: 31 }, (_, i) => i + 1);

const ALLERGY_OPTIONS = [
  { value: 'wheat',     label: '小麦' },
  { value: 'egg',       label: '卵' },
  { value: 'milk',      label: '乳' },
  { value: 'shrimp',    label: 'えび' },
  { value: 'crab',      label: 'かに' },
  { value: 'buckwheat', label: 'そば' },
  { value: 'peanut',    label: '落花生' },
  { value: 'other',     label: 'その他' },
];

const HISTORY_OPTIONS = [
  { value: 'pneumonia',               label: '肺炎' },
  { value: 'heart_disease',           label: '心臓病' },
  { value: 'convulsion',              label: 'けいれん' },
  { value: 'atopic_dermatitis',       label: 'アトピー性皮膚炎' },
  { value: 'congenital_disease',      label: '先天性疾患' },
  { value: 'urinary_tract_infection', label: '尿路感染症' },
  { value: 'kawasaki_disease',        label: '川崎病' },
  { value: 'developmental_disorder',  label: '発達障害' },
  { value: 'asthma',                  label: '喘息' },
  { value: 'hospitalization_surgery', label: '入院・手術' },
  { value: 'other',                   label: 'その他' },
];

const STEP_LABELS = ['基本情報', 'アレルギー', '保険証', '医療証'];

const labelStyle = {
  display: 'block',
  fontSize: '13px',
  fontWeight: '600',
  color: '#374151',
  marginBottom: '6px',
};

const reqMark = <span style={{ color: '#ef4444', marginLeft: '2px', fontSize: '12px' }}>*</span>;

const selectStyle = {
  flex: 1,
  padding: '10px 8px',
  border: '1.5px solid #e5e7eb',
  borderRadius: '8px',
  fontSize: '15px',
  background: '#fff',
  color: '#374151',
  WebkitAppearance: 'none',
  appearance: 'none',
  cursor: 'pointer',
  outline: 'none',
  fontFamily: 'inherit',
};

function Chip({ label, checked, onChange, disabled }) {
  return (
    <button
      type="button"
      onClick={onChange}
      disabled={disabled}
      style={{
        padding: '6px 12px',
        border: `1.5px solid ${checked ? '#4f46e5' : '#e5e7eb'}`,
        borderRadius: '20px',
        background: checked ? '#eff6ff' : '#f9fafb',
        color: checked ? '#4f46e5' : '#374151',
        fontSize: '13px',
        fontWeight: checked ? '600' : '400',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.15s',
        whiteSpace: 'nowrap',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {label}
    </button>
  );
}

function StepIndicator({ step }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
      {STEP_LABELS.map((label, i) => {
        const active = i === step;
        const done   = i < step;
        return (
          <React.Fragment key={i}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
              <div style={{
                width: '28px', height: '28px', borderRadius: '50%',
                background: (done || active) ? '#4f46e5' : '#e5e7eb',
                color: (done || active) ? 'white' : '#9ca3af',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '13px', fontWeight: '700', marginBottom: '4px',
                transition: 'all 0.2s',
              }}>
                {done ? '✓' : i + 1}
              </div>
              <span style={{
                fontSize: '10px',
                color: (active || done) ? '#4f46e5' : '#9ca3af',
                fontWeight: active ? '600' : '400',
                whiteSpace: 'nowrap',
              }}>
                {label}
              </span>
            </div>
            {i < STEP_LABELS.length - 1 && (
              <div style={{
                height: '2px', flex: 1,
                background: done ? '#4f46e5' : '#e5e7eb',
                marginBottom: '16px', transition: 'all 0.2s',
              }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

const dropZoneStyle = (hasFile) => ({
  flex: 1,
  minHeight: '120px',
  border: `2px dashed ${hasFile ? '#4f46e5' : '#d1d5db'}`,
  borderRadius: '12px',
  background: hasFile ? '#f5f3ff' : '#fafafa',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s',
  position: 'relative',
  overflow: 'hidden',
});

function genderBtn(label, val, current, onSelect, disabled) {
  const active = current === val;
  return (
    <button
      key={val}
      type="button"
      onClick={() => onSelect(val)}
      disabled={disabled}
      style={{
        flex: 1, padding: '10px 6px',
        border: `1.5px solid ${active ? '#4f46e5' : '#e5e7eb'}`,
        borderRadius: '8px',
        background: active ? '#eff6ff' : '#f9fafb',
        color: active ? '#4f46e5' : '#6b7280',
        fontSize: '14px', fontWeight: active ? '600' : '400',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.15s',
      }}
    >
      {label}
    </button>
  );
}

function FooterBtn({ label, onClick, isPrimary, isDisabled, isBusy }) {
  return (
    <button
      type={isPrimary ? 'submit' : 'button'}
      onClick={onClick}
      disabled={isDisabled}
      style={{
        flex: isPrimary ? 2 : 1,
        padding: '13px',
        border: isPrimary ? 'none' : '1.5px solid #e5e7eb',
        borderRadius: '10px',
        background: isPrimary
          ? isDisabled ? '#e5e7eb' : 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)'
          : '#f9fafb',
        color: isPrimary ? (isDisabled ? '#9ca3af' : 'white') : '#6b7280',
        fontSize: '15px', fontWeight: isPrimary ? '600' : '500',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s',
      }}
    >
      {isBusy ? '保存中…' : label}
    </button>
  );
}

function parseBirthDate(dateStr) {
  if (!dateStr) return { year: '', month: '', day: '' };
  const [y, m, d] = dateStr.split('-');
  return { year: String(parseInt(y, 10)), month: String(parseInt(m, 10)), day: String(parseInt(d, 10)) };
}

const ChildProfileModal = ({ open, onClose, onSaved, initialProfile }) => {
  const isEdit = !!initialProfile?.id;
  const [step, setStep] = useState(0);
  const [initialized, setInitialized] = useState(false);

  // Step 0: 基本情報
  const [lastName,      setLastName]      = useState('');
  const [firstName,     setFirstName]     = useState('');
  const [lastNameKana,  setLastNameKana]  = useState('');
  const [firstNameKana, setFirstNameKana] = useState('');
  const [birthYear,     setBirthYear]     = useState('');
  const [birthMonth,    setBirthMonth]    = useState('');
  const [birthDay,      setBirthDay]      = useState('');

  // Step 1: アレルギー・既往歴
  const [gender,            setGender]            = useState('');
  const [selectedAllergies, setSelectedAllergies] = useState([]);
  const [allergyOther,      setAllergyOther]      = useState('');
  const [selectedHistory,   setSelectedHistory]   = useState([]);
  const [historyOther,      setHistoryOther]      = useState('');

  // Step 2: 保険証
  const [insuranceFront, setInsuranceFront] = useState(null);
  const [insuranceBack, setInsuranceBack] = useState(null);
  const insuranceFrontRef = useRef(null);
  const insuranceBackRef = useRef(null);

  // Step 3: 医療証
  const [cardFront, setCardFront] = useState(null);
  const [cardBack, setCardBack] = useState(null);
  const frontRef = useRef(null);
  const backRef = useRef(null);

  const [saving, setSaving]   = useState(false);
  const [error,  setError]    = useState('');
  const [alertModal, setAlertModal] = useState({ open: false, title: '', message: '' });

  useEffect(() => {
    if (!open) return;

    if (!initialized) {
      setStep(0);
      setError('');
      if (isEdit && initialProfile) {
        const bd = parseBirthDate(initialProfile.birth_date);
        setLastName(initialProfile.last_name || '');
        setFirstName(initialProfile.first_name || '');
        setLastNameKana(initialProfile.last_name_kana || '');
        setFirstNameKana(initialProfile.first_name_kana || '');
        setBirthYear(bd.year);
        setBirthMonth(bd.month);
        setBirthDay(bd.day);
        setGender(initialProfile.gender || '');
        const allergies = [];
        if (!initialProfile.has_allergy) { allergies.push('none'); }
        else {
          ['wheat','egg','milk','shrimp','crab','buckwheat','peanut'].forEach(k => {
            if (initialProfile[`allergy_${k}`]) allergies.push(k);
          });
          if (initialProfile.allergy_other) { allergies.push('other'); setAllergyOther(initialProfile.allergy_other); }
        }
        setSelectedAllergies(allergies);
        const history = [];
        if (!initialProfile.has_medical_history) { history.push('none'); }
        else {
          ['pneumonia','heart_disease','convulsion','atopic_dermatitis','congenital_disease',
           'urinary_tract_infection','kawasaki_disease','developmental_disorder','asthma','hospitalization_surgery'].forEach(k => {
            if (initialProfile[`history_${k}`]) history.push(k);
          });
          if (initialProfile.history_other) { history.push('other'); setHistoryOther(initialProfile.history_other); }
        }
        setSelectedHistory(history);
      } else {
        setLastName(''); setFirstName('');
        setLastNameKana(''); setFirstNameKana('');
        setBirthYear(''); setBirthMonth(''); setBirthDay('');
        setGender('');
        setSelectedAllergies([]); setAllergyOther('');
        setSelectedHistory([]); setHistoryOther('');
      }
      setInsuranceFront(null);
      setInsuranceBack(null);
      setCardFront(null);
      setCardBack(null);
      setInitialized(true);
    }
  }, [open, isEdit, initialProfile, initialized]);

  useEffect(() => {
    setInitialized(false);
  }, [initialProfile]);

  const isKatakana = (s) => /^[ァ-ヶー\s]*$/.test(s.trim());

  const step0Valid = useMemo(() => {
    if (!lastName.trim() || !firstName.trim()) return false;
    if (!lastNameKana.trim() || !firstNameKana.trim()) return false;
    if (!isKatakana(lastNameKana) || !isKatakana(firstNameKana)) return false;
    if (!birthYear || !birthMonth || !birthDay) return false;
    return true;
  }, [lastName, firstName, lastNameKana, firstNameKana, birthYear, birthMonth, birthDay]);

  const step1Valid = !!gender && (selectedAllergies.includes('none') || selectedAllergies.length > 0);
  const step2Valid = !!insuranceFront && !!insuranceBack;
  const step3Valid = !!cardFront; // 表面のみ必須、裏面は任意

  const goToStep2 = (e) => {
    e?.preventDefault();
    if (!step1Valid) return;
    setStep(2);
  };

  const submitAll = async () => {
    if (!step3Valid || saving) return;
    setSaving(true);
    setError('');

    try {
      // 1. プロフィール保存
      const payload = {
        child_profile: {
          last_name: lastName.trim(), first_name: firstName.trim(),
          last_name_kana: lastNameKana.trim(), first_name_kana: firstNameKana.trim(),
          birth_date: `${birthYear}-${String(birthMonth).padStart(2,'0')}-${String(birthDay).padStart(2,'0')}`,
          gender,
          has_allergy: !selectedAllergies.includes('none') && selectedAllergies.length > 0,
          allergy_wheat:      selectedAllergies.includes('wheat'),
          allergy_egg:        selectedAllergies.includes('egg'),
          allergy_milk:       selectedAllergies.includes('milk'),
          allergy_shrimp:     selectedAllergies.includes('shrimp'),
          allergy_crab:       selectedAllergies.includes('crab'),
          allergy_buckwheat:  selectedAllergies.includes('buckwheat'),
          allergy_peanut:     selectedAllergies.includes('peanut'),
          allergy_other:      selectedAllergies.includes('other') ? (allergyOther.trim() || null) : null,
          has_medical_history: selectedHistory.filter(v => v !== 'none').length > 0,
          history_pneumonia:               selectedHistory.includes('pneumonia'),
          history_heart_disease:           selectedHistory.includes('heart_disease'),
          history_convulsion:              selectedHistory.includes('convulsion'),
          history_atopic_dermatitis:       selectedHistory.includes('atopic_dermatitis'),
          history_congenital_disease:      selectedHistory.includes('congenital_disease'),
          history_urinary_tract_infection: selectedHistory.includes('urinary_tract_infection'),
          history_kawasaki_disease:        selectedHistory.includes('kawasaki_disease'),
          history_developmental_disorder:  selectedHistory.includes('developmental_disorder'),
          history_asthma:                  selectedHistory.includes('asthma'),
          history_hospitalization_surgery: selectedHistory.includes('hospitalization_surgery'),
          history_other: selectedHistory.includes('other') ? (historyOther.trim() || null) : null,
        },
      };

      const profileRes = await (isEdit
        ? axiosInstance.put(`/api/medichat/child_profiles/${initialProfile.id}`, payload)
        : axiosInstance.post('/api/medichat/child_profiles', payload)
      );
      const profileId = profileRes.data?.child_profile?.id || initialProfile?.id;

      // 2. 保険証アップロード
      let insFront = insuranceFront;
      let insBack = insuranceBack;
      if (isHeicFile(insFront)) insFront = await convertHeicToJpeg(insFront);
      if (isHeicFile(insBack)) insBack = await convertHeicToJpeg(insBack);

      const insFd = new FormData();
      insFd.append('front', insFront);
      insFd.append('back', insBack);
      insFd.append('child_profile_id', profileId);
      await axiosInstance.post('/api/medichat/insurance_cards/upload', insFd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // 3. 医療証アップロード
      let aidFront = cardFront;
      let aidBack = cardBack;
      if (isHeicFile(aidFront)) aidFront = await convertHeicToJpeg(aidFront);
      if (isHeicFile(aidBack)) aidBack = await convertHeicToJpeg(aidBack);

      const aidFd = new FormData();
      aidFd.append('front', aidFront);
      aidFd.append('back', aidBack);
      await axiosInstance.post(
        `/api/medichat/child_profiles/${profileId}/child_medical_aids/upload`,
        aidFd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setSaving(false);
      setInitialized(false);
      onSaved?.();
      onClose?.();
    } catch (err) {
      setSaving(false);
      const msg = err?.response?.data?.error || err?.response?.data?.message || '登録に失敗しました';
      setAlertModal({ open: true, title: 'エラー', message: msg });
    }
  };

  if (!open) return null;

  const title = isEdit ? 'お子さまの情報を編集' : 'お子さまの登録';

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(15,23,42,0.5)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      zIndex: 10000,
    }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        width: '100%', maxWidth: '500px',
        maxHeight: '90vh',
        background: 'white',
        borderRadius: '20px 20px 0 0',
        boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}>
        {/* ヘッダー */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <span style={{ fontSize: '16px', fontWeight: '600', color: '#1a1a1a' }}>{title}</span>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#666', padding: '4px' }}
          >✕</button>
        </div>

        {/* ボディ（スクロール） */}
        <div style={{ flex: 1, overflow: 'hidden', minHeight: 0, padding: '16px 20px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>

            {/* STEP 0: 基本情報 */}
            {step === 0 && (
              <>
                <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '18px', paddingBottom: '8px' }}>
                  <StepIndicator step={0} />

                  <div>
                    <label style={labelStyle}>お名前 {reqMark}</label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                      <input style={{ ...selectStyle, flex: 'none', width: '100%' }} type="text" placeholder="姓"
                        value={lastName} onChange={e => setLastName(e.target.value)} disabled={saving} />
                      <input style={{ ...selectStyle, flex: 'none', width: '100%' }} type="text" placeholder="名"
                        value={firstName} onChange={e => setFirstName(e.target.value)} disabled={saving} />
                    </div>
                  </div>

                  <div>
                    <label style={labelStyle}>フリガナ（全角カタカナ） {reqMark}</label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                      <input style={{ ...selectStyle, flex: 'none', width: '100%' }} type="text" placeholder="セイ"
                        value={lastNameKana} onChange={e => setLastNameKana(e.target.value)} disabled={saving} />
                      <input style={{ ...selectStyle, flex: 'none', width: '100%' }} type="text" placeholder="メイ"
                        value={firstNameKana} onChange={e => setFirstNameKana(e.target.value)} disabled={saving} />
                    </div>
                    {((lastNameKana && !isKatakana(lastNameKana)) || (firstNameKana && !isKatakana(firstNameKana))) && (
                      <p style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>全角カタカナで入力してください</p>
                    )}
                  </div>

                  <div>
                    <label style={labelStyle}>生年月日 {reqMark}</label>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '8px' }}>
                      <select style={selectStyle} value={birthYear} onChange={e => setBirthYear(e.target.value)} disabled={saving}>
                        <option value="">年</option>
                        {YEARS.map(y => <option key={y} value={y}>{y}年</option>)}
                      </select>
                      <select style={selectStyle} value={birthMonth} onChange={e => setBirthMonth(e.target.value)} disabled={saving}>
                        <option value="">月</option>
                        {MONTHS.map(m => <option key={m} value={m}>{m}月</option>)}
                      </select>
                      <select style={selectStyle} value={birthDay} onChange={e => setBirthDay(e.target.value)} disabled={saving}>
                        <option value="">日</option>
                        {DAYS.map(d => <option key={d} value={d}>{d}日</option>)}
                      </select>
                    </div>
                  </div>
                </div>

                <div style={{ paddingTop: '12px', borderTop: '1px solid #f0f0f0', display: 'flex', gap: '10px', flexShrink: 0 }}>
                  <FooterBtn label="キャンセル" onClick={onClose} isPrimary={false} isDisabled={saving} />
                  <FooterBtn label="次へ →" onClick={() => setStep(1)} isPrimary isDisabled={!step0Valid} />
                </div>
              </>
            )}

            {/* STEP 1: アレルギー・既往歴 */}
            {step === 1 && (
              <form onSubmit={goToStep2} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '18px', paddingBottom: '8px' }}>
                  <StepIndicator step={1} />

                  <div>
                    <label style={labelStyle}>性別 {reqMark}</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {genderBtn('男の子', 'male',   gender, setGender, saving)}
                      {genderBtn('女の子', 'female', gender, setGender, saving)}
                      {genderBtn('その他', 'other',  gender, setGender, saving)}
                    </div>
                  </div>

                  <div>
                    <label style={labelStyle}>アレルギー {reqMark}</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '4px' }}>
                      <Chip label="なし（該当なし）" checked={selectedAllergies.includes('none')}
                        disabled={saving}
                        onChange={() => {
                          if (selectedAllergies.includes('none')) setSelectedAllergies([]);
                          else setSelectedAllergies(['none']);
                        }} />
                      {ALLERGY_OPTIONS.map(o => (
                        <Chip key={o.value} label={o.label} disabled={saving}
                          checked={selectedAllergies.includes(o.value)}
                          onChange={() => setSelectedAllergies(p => {
                            const without = p.filter(v => v !== 'none');
                            return without.includes(o.value) ? without.filter(v => v !== o.value) : [...without, o.value];
                          })} />
                      ))}
                    </div>
                    {selectedAllergies.includes('other') && (
                      <input style={{ ...selectStyle, flex: 'none', width: '100%', marginTop: '8px' }}
                        type="text" placeholder="アレルギーの内容を入力"
                        value={allergyOther} onChange={e => setAllergyOther(e.target.value)}
                        disabled={saving} autoFocus />
                    )}
                  </div>

                  <div>
                    <label style={labelStyle}>
                      既往歴・持病{' '}
                      <span style={{ fontSize: '11px', color: '#9ca3af', fontWeight: '400' }}>（任意）</span>
                    </label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      <Chip label="なし（該当なし）" disabled={saving}
                        checked={selectedHistory.includes('none')}
                        onChange={() => setSelectedHistory(p => p.includes('none') ? [] : ['none'])} />
                      {HISTORY_OPTIONS.map(o => (
                        <Chip key={o.value} label={o.label} disabled={saving}
                          checked={selectedHistory.includes(o.value)}
                          onChange={() => setSelectedHistory(p => {
                            const without = p.filter(v => v !== 'none');
                            return without.includes(o.value) ? without.filter(v => v !== o.value) : [...without, o.value];
                          })} />
                      ))}
                    </div>
                    {selectedHistory.includes('other') && (
                      <input style={{ ...selectStyle, flex: 'none', width: '100%', marginTop: '8px' }}
                        type="text" placeholder="既往歴・持病の内容を入力"
                        value={historyOther} onChange={e => setHistoryOther(e.target.value)}
                        disabled={saving} autoFocus />
                    )}
                  </div>

                  {error && (
                    <p style={{ fontSize: '13px', color: '#ef4444', padding: '8px 12px', background: '#fef2f2', borderRadius: '8px', margin: 0 }}>
                      {error}
                    </p>
                  )}
                </div>

                <div style={{ paddingTop: '12px', borderTop: '1px solid #f0f0f0', display: 'flex', gap: '10px', flexShrink: 0 }}>
                  <FooterBtn label="← 戻る" onClick={() => setStep(0)} isPrimary={false} isDisabled={saving} />
                  <FooterBtn
                    label={saving ? '保存中…' : '次へ →'}
                    isPrimary
                    isDisabled={!step1Valid || saving}
                    isBusy={saving}
                  />
                </div>
              </form>
            )}

            {/* STEP 2: 保険証 */}
            {step === 2 && (
              <>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                  <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '12px', paddingBottom: '12px' }}>
                    <StepIndicator step={2} />

                    <div style={{
                      padding: '12px 14px',
                      backgroundColor: '#f0f9ff',
                      borderLeft: '4px solid #3b82f6',
                      borderRadius: '4px',
                      fontSize: '15px',
                      lineHeight: '1.75',
                      textAlign: 'left',
                    }}>
                      <p style={{ marginBottom: 0, color: '#1e40af', fontSize: '15px', fontWeight: 600 }}>
                        🏥 お子さまの保険証（マイナ保険証情報・資格確認書）を登録してください。表面・裏面の両方が必須です。
                      </p>
                    </div>
                  </div>

                  <div style={{ 
                    flex: 1, 
                    overflowX: 'auto', 
                    overflowY: 'hidden',
                    WebkitOverflowScrolling: 'touch',
                    paddingBottom: '8px'
                  }}>
                    <div style={{ display: 'flex', gap: '16px', minWidth: 'max-content', padding: '0 4px' }}>
                      <input
                        ref={insuranceFrontRef}
                        type="file"
                        accept="image/*,.heic,.heif"
                        style={{ display: 'none' }}
                        onChange={(e) => setInsuranceFront(e.target.files?.[0] || null)}
                      />
                      <div style={{ width: '160px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                          表面 {reqMark}
                        </div>
                        <button
                          type="button"
                          onClick={() => insuranceFrontRef.current?.click()}
                          style={{ ...dropZoneStyle(!!insuranceFront), width: '160px', height: '100px' }}
                        >
                          {insuranceFront ? (
                            <img
                              src={URL.createObjectURL(insuranceFront)}
                              alt="保険証表"
                              style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0 }}
                            />
                          ) : (
                            <>
                              <div style={{ fontSize: '24px', marginBottom: '8px' }}>📷</div>
                              <div style={{ fontSize: '12px', color: '#6b7280' }}>タップして撮影</div>
                            </>
                          )}
                        </button>
                      </div>

                      <input
                        ref={insuranceBackRef}
                        type="file"
                        accept="image/*,.heic,.heif"
                        style={{ display: 'none' }}
                        onChange={(e) => setInsuranceBack(e.target.files?.[0] || null)}
                      />
                      <div style={{ width: '160px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                          裏面 {reqMark}
                        </div>
                        <button
                          type="button"
                          onClick={() => insuranceBackRef.current?.click()}
                          style={{ ...dropZoneStyle(!!insuranceBack), width: '160px', height: '100px' }}
                        >
                          {insuranceBack ? (
                            <img
                              src={URL.createObjectURL(insuranceBack)}
                              alt="保険証裏"
                              style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0 }}
                            />
                          ) : (
                            <>
                              <div style={{ fontSize: '24px', marginBottom: '8px' }}>📷</div>
                              <div style={{ fontSize: '12px', color: '#6b7280' }}>タップして撮影</div>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ paddingTop: '12px', borderTop: '1px solid #f0f0f0', display: 'flex', gap: '10px', flexShrink: 0 }}>
                  <FooterBtn label="← 戻る" onClick={() => setStep(1)} isPrimary={false} isDisabled={saving} />
                  <FooterBtn
                    label="次へ →"
                    onClick={() => setStep(3)}
                    isPrimary
                    isDisabled={!step2Valid}
                  />
                </div>
              </>
            )}

            {/* STEP 3: 医療証 */}
            {step === 3 && (
              <>
                <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '18px', paddingBottom: '8px' }}>
                  <StepIndicator step={3} />

                  <div style={{ padding: '10px 14px', background: '#eff6ff', borderRadius: '10px', fontSize: '13px', color: '#1e40af', lineHeight: '1.5' }}>
                    🏥 子ども医療費助成証をお持ちの場合はアップロードしてください。診察時に自動で適用されます。
                  </div>
                  <div style={{ padding: '12px 14px', background: '#fff7ed', borderRadius: '10px', fontSize: '13px', fontWeight: 500, color: '#9a3412', lineHeight: '1.7', border: '1px solid #fdba74' }}>
                    ※ 東京都外在住の方
                    <br />
                    一度自己負担金を支払いいただき、お住まいの自治体への還付申請を行なっていただく必要があります。
                  </div>

                  <p style={{ fontSize: '11px', color: '#6b7280', margin: 0, textAlign: 'center' }}>
                    ※必要情報がすべて見えるように広げて撮影してください
                  </p>

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <input
                      ref={frontRef}
                      type="file"
                      accept="image/*,.heic,.heif"
                      style={{ display: 'none' }}
                      onChange={(e) => setCardFront(e.target.files?.[0] || null)}
                    />
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                        表面 {reqMark}
                      </div>
                      <button
                        type="button"
                        onClick={() => frontRef.current?.click()}
                        style={dropZoneStyle(!!cardFront)}
                      >
                        {cardFront ? (
                          <img
                            src={URL.createObjectURL(cardFront)}
                            alt="医療証表"
                            style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0 }}
                          />
                        ) : (
                          <>
                            <div style={{ fontSize: '24px', marginBottom: '8px' }}>📷</div>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>タップして撮影</div>
                          </>
                        )}
                      </button>
                    </div>

                    <input
                      ref={backRef}
                      type="file"
                      accept="image/*,.heic,.heif"
                      style={{ display: 'none' }}
                      onChange={(e) => setCardBack(e.target.files?.[0] || null)}
                    />
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                        裏面 <span style={{ fontSize: '10px', color: '#9ca3af', fontWeight: '400' }}>（任意）</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => backRef.current?.click()}
                        style={dropZoneStyle(!!cardBack)}
                      >
                        {cardBack ? (
                          <img
                            src={URL.createObjectURL(cardBack)}
                            alt="医療証裏"
                            style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0 }}
                          />
                        ) : (
                          <>
                            <div style={{ fontSize: '24px', marginBottom: '8px' }}>📷</div>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>タップして撮影</div>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                <div style={{ paddingTop: '12px', borderTop: '1px solid #f0f0f0', display: 'flex', gap: '10px', flexShrink: 0 }}>
                  <FooterBtn label="← 戻る" onClick={() => setStep(2)} isPrimary={false} isDisabled={saving} />
                  <FooterBtn
                    label={saving ? '登録中…' : '登録を完了する'}
                    onClick={submitAll}
                    isPrimary
                    isDisabled={!step3Valid || saving}
                    isBusy={saving}
                  />
                </div>
              </>
            )}

          </div>
        </div>
      </div>

      <AlertModal
        open={alertModal.open}
        title={alertModal.title}
        message={alertModal.message}
        onClose={() => setAlertModal({ open: false, title: '', message: '' })}
      />
    </div>
  );
};

export default ChildProfileModal;
